<template>
  <div v-if="sanctions.length && props.showSanctionsBlock === true" class="mt-6 nobreak">
    <h3 class="text-lg font-medium text-gray-800 mb-3">
      {{ $gettext("Sanctions") }}
    </h3>

    <div v-for="exec in sanctions" :key="exec.id" class="mb-3 p-3 border"
      :class="exec.attributes.score > 0 ? 'border-red-400' : 'border-green-400'">
      <div class="flex items-center justify-between">
        <span class="font-medium text-gray-800">
          {{ exec.attributes.justification }}
        </span>
        <span class="px-3 py-1 text-sm font-semibold"
          :class="exec.attributes.score > 0 ? 'text-red-500' : 'text-green-500'">
          {{ exec.attributes.score > 0 ? $gettext("Sanctionné !") : $gettext("Pas sanctionné") }}
        </span>
      </div>
      <div class="mt-1 text-sm text-gray-600">
        {{ $gettext("Score:") }} {{ exec.attributes.score > 0 ? exec.attributes.score : 0 }}
      </div>
      <div class="mt-1 text-xs text-gray-400 italic">
        {{ $gettext("Vérifié le") }} {{ parseDate(exec.attributes.createdAt) }}
      </div>
    </div>
  </div>
  <div class="nobreak">
    <h3 class="text-lg font-medium text-gray-800 mb-2">{{ $gettext("Profil de risque") }}</h3>
    <div v-if="riskScoring" class="mt-4 mb-4 mr-4" style="transform:none;">
      <div :class="badgeClass" :style="badgeStyle" class="text-xl" role="status" aria-label="risk-scoring-badge">
        {{ badgeText }}
      </div>
      <div class="mt-4">
        {{ $gettext("Score: ") }} {{ riskScoring.attributes.score }}
      </div>
    </div>
    <div v-else class="text-sm text-gray-500">
      {{ $gettext("Aucun profil de risque disponible.") }}
    </div>
    <div v-if="riskScoring?.relationships?.riskScoringRuleExecutions?.data?.length">
      <h3 class="text-lg font-medium text-gray-800 mb-3">
        {{ $gettext("Détails du profil de risque") }}
      </h3>
      <table class="w-full border border-gray-200 text-sm">
        <thead class="bg-gray-50">
          <tr>
            <th class="text-left px-3 py-2 border-b border-gray-200">{{ $gettext("Règle") }}</th>
            <th class="text-center px-3 py-2 border-b border-gray-200 w-20">{{ $gettext("Score") }}</th>
            <th class="text-center px-3 py-2 border-b border-gray-200 w-28">{{ $gettext("Niveau") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="exec in filteredExecutions" :key="exec.id" class="odd:bg-white even:bg-gray-50">
            <td class="px-3 py-2 border-b border-gray-200">{{ exec.attributes.justification }}</td>
            <td class="px-3 py-2 border-b border-gray-200 text-center font-medium">{{ exec.attributes.score }}</td>
            <td class="px-3 py-2 border-b border-gray-200 text-center">
              <span :class="badgeClassFor(exec.attributes.level)" :style="badgeStyle">
                {{ riskLevelLabels[exec.attributes.level] }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useGettext } from "vue3-gettext";
import { useRiskBadges } from "@/utils/reports_badges.js";
import { useDate } from "@/utils/stores/date";

const { parseDate } = useDate();

const { badgeClassFor } = useRiskBadges();
const { $gettext } = useGettext();
const props = defineProps({
  riskScoring: Object,
  showSanctionsBlock: Boolean,
});

const levelKey = computed(() =>
  props.riskScoring?.attributes?.scoreLevel ?? 'undefined'
);

const filteredExecutions = computed(() =>
  props.riskScoring?.relationships?.riskScoringRuleExecutions?.data?.filter(
    (exec) => exec.relationships?.riskScoringRule?.data?.attributes?.criticality !== "sanction"
  ) ?? []
);

const sanctions = computed(() =>
  props.riskScoring?.relationships?.riskScoringRuleExecutions?.data?.filter(
    (exec) => exec.relationships?.riskScoringRule?.data?.attributes?.criticality === "sanction"
  ) ?? []
)

const badgeText = computed(() => {
  const key = levelKey.value;
  return riskLevelLabels[key] || riskLevelLabels["undefined"];
});

const badgeClass = computed(() => badgeClassFor(levelKey.value));

const badgeStyle = {
  '-webkit-print-color-adjust': 'exact',
  'print-color-adjust': 'exact',
  'line-height': '1'
};

const riskLevelLabels = {
  low: $gettext("Faible"),
  medium: $gettext("Standard"),
  high: $gettext("Élevé"),
  undefined: $gettext("Inconnu"),
};
</script>
